/**
 * Student-friendly palette proposals for the StaxED v2 portal.
 *
 * Each option uses the same structural language as the current v2 (rounded
 * cards, soft shadows, brand-tinted hover/active states, Inter typography) —
 * the differences are PALETTE and dark-mode treatment. The styleguide page
 * applies these per-panel via inline CSS variables.
 *
 * Dark-mode principles (the bit the user has called out twice as feeling
 * AI-vibe-coded):
 * - Background is never pure #000 and never the generic cool-slate #0F1117.
 *   It carries a slight hue undertone matching the brand family.
 * - Four surface levels (bg → surface → surface-2 → surface-3) so the UI
 *   has real depth, not the flat "two greys" most AI palettes ship.
 * - The dark-mode brand is a HUE-SHIFTED version of the light-mode brand,
 *   not just "same hue, lifted lightness". This is what makes dark modes
 *   feel intentional vs auto-generated.
 * - Status colours (success/danger/info) are dialed back a notch in dark
 *   mode so they don't read as neon.
 */

export type ThemeMode = "light" | "dark";

export type StyleTheme = {
  id: string;
  name: string;
  tagline: string;
  description: string;
  accentNote: string;
  vibes: string[];
  light: TokenSet;
  dark: TokenSet;
};

type TokenSet = {
  bg: string;
  surface: string;
  surface2: string;
  surface3: string;
  border: string;
  borderStrong: string;
  text: string;
  textMuted: string;
  textSubtle: string;
  brand: string;
  brandHover: string;
  brandSoft: string;
  brandSoftStrong: string;
  accent: string;
  accentSoft: string;
  success: string;
  successSoft: string;
  danger: string;
  dangerSoft: string;
  info: string;
  ring: string;
};

export function tokenToCssVars(tokens: TokenSet): Record<string, string> {
  return {
    "--bg":                 tokens.bg,
    "--surface":            tokens.surface,
    "--surface-2":          tokens.surface2,
    "--surface-3":          tokens.surface3,
    "--border":             tokens.border,
    "--border-strong":      tokens.borderStrong,
    "--text":               tokens.text,
    "--text-muted":         tokens.textMuted,
    "--text-subtle":        tokens.textSubtle,
    "--brand":              tokens.brand,
    "--brand-hover":        tokens.brandHover,
    "--brand-soft":         tokens.brandSoft,
    "--brand-soft-strong":  tokens.brandSoftStrong,
    "--accent":             tokens.accent,
    "--accent-soft":        tokens.accentSoft,
    "--success":            tokens.success,
    "--success-soft":       tokens.successSoft,
    "--danger":             tokens.danger,
    "--danger-soft":        tokens.dangerSoft,
    "--info":               tokens.info,
    "--ring":               tokens.ring,
  };
}

// ---------------------------------------------------------------------------
// INDIGO -- the current v2 design, dark mode refined.
//
// Light mode unchanged.
// Dark mode: warmer near-black with a slight indigo undertone (#0B0E1A), a
//   proper 4-step surface scale, and a periwinkle accent shifted slightly off
//   the light-mode hue so the two modes feel related but each tuned for its
//   environment. Status colours toned down so they read as info-rich rather
//   than neon.
// ---------------------------------------------------------------------------
export const INDIGO: StyleTheme = {
  id: "indigo",
  name: "Indigo",
  tagline: "Classic, calm, the safe pick. Refined dark mode.",
  description:
    "The current v2 direction. Trusted SaaS indigo, soft-tinted hovers. Light mode is unchanged; the dark mode now uses a slightly warmer near-black, four real surface levels, and a periwinkle accent that breathes against the deep ground rather than glowing on it.",
  accentNote: "Accent: indigo. Soft-tinted hovers and active states.",
  vibes: ["Calm", "Familiar", "Trustworthy", "Safe"],
  light: {
    bg:               "250 250 251",
    surface:          "255 255 255",
    surface2:         "244 244 246",
    surface3:         "234 234 238",
    border:           "229 231 235",
    borderStrong:     "209 213 219",
    text:             "15 23 42",
    textMuted:        "71 85 105",
    textSubtle:       "148 163 184",
    brand:            "79 70 229",
    brandHover:       "67 56 202",
    brandSoft:        "238 242 255",
    brandSoftStrong:  "224 231 255",
    accent:           "217 119 6",
    accentSoft:       "254 243 199",
    success:          "5 150 105",
    successSoft:      "209 250 229",
    danger:           "220 38 38",
    dangerSoft:       "254 226 226",
    info:             "2 132 199",
    ring:             "165 180 252",
  },
  dark: {
    bg:               "11 14 26",       // #0B0E1A warmer than the old slate
    surface:          "21 24 38",       // #151826
    surface2:         "30 34 54",       // #1E2236
    surface3:         "42 47 75",       // #2A2F4B
    border:           "30 34 54",
    borderStrong:     "60 67 100",
    text:             "240 242 250",
    textMuted:        "165 173 196",
    textSubtle:       "108 116 140",
    brand:            "139 148 245",    // #8B94F5 periwinkle, slight pink shift
    brandHover:       "175 183 255",
    brandSoft:        "29 30 65",
    brandSoftStrong:  "44 47 100",
    accent:           "245 180 88",
    accentSoft:       "60 42 12",
    success:          "76 200 142",
    successSoft:      "10 38 28",
    danger:           "240 122 122",
    dangerSoft:       "50 18 18",
    info:             "98 178 232",
    ring:             "110 122 220",
  },
};

// ---------------------------------------------------------------------------
// COBALT -- educational sky blue. Khan Academy / Brilliant adjacent without
//   copying. Reads as approachable + serious. Light is crisp; dark is a deep
//   ocean blue with a buoyant cyan accent that still passes contrast.
// ---------------------------------------------------------------------------
export const COBALT: StyleTheme = {
  id: "cobalt",
  name: "Cobalt",
  tagline: "Bright, awake, classic edtech.",
  description:
    "Sky-blue with intent. Cleaner and more wake-me-up than indigo, while staying serious enough for exam prep. Dark mode is a deep oceanic background with a soft cyan-shifted accent — feels alert, not loud.",
  accentNote: "Accent: cobalt sky blue.",
  vibes: ["Bright", "Studious", "Approachable", "Edtech-classic"],
  light: {
    bg:               "249 250 252",
    surface:          "255 255 255",
    surface2:         "241 245 249",
    surface3:         "226 232 240",
    border:           "226 232 240",
    borderStrong:     "203 213 225",
    text:             "12 22 37",
    textMuted:        "71 85 105",
    textSubtle:       "148 163 184",
    brand:            "14 165 233",     // #0EA5E9 sky-500
    brandHover:       "2 132 199",
    brandSoft:        "224 242 254",
    brandSoftStrong:  "186 230 253",
    accent:           "234 88 12",
    accentSoft:       "255 237 213",
    success:          "5 150 105",
    successSoft:      "209 250 229",
    danger:           "220 38 38",
    dangerSoft:       "254 226 226",
    info:             "14 165 233",
    ring:             "125 211 252",
  },
  dark: {
    bg:               "8 18 28",        // #08121C deep ocean
    surface:          "17 28 41",       // #111C29
    surface2:         "25 40 58",       // #19283A
    surface3:         "35 57 84",
    border:           "25 40 58",
    borderStrong:     "59 88 122",
    text:             "236 245 252",
    textMuted:        "160 180 200",
    textSubtle:       "100 122 145",
    brand:            "125 211 252",    // #7DD3FC sky-300
    brandHover:       "165 226 254",
    brandSoft:        "11 36 56",
    brandSoftStrong:  "20 58 86",
    accent:           "251 191 36",
    accentSoft:       "60 42 12",
    success:          "76 200 142",
    successSoft:      "10 38 28",
    danger:           "240 122 122",
    dangerSoft:       "50 18 18",
    info:             "125 211 252",
    ring:             "56 189 248",
  },
};

// ---------------------------------------------------------------------------
// EMERALD -- fresh academic green. Reads as growth, focus, calm energy.
//   Light mode is bright like Notion-academy; dark mode pulls toward a near-
//   black with a green undertone and a mint-shifted accent.
// ---------------------------------------------------------------------------
export const EMERALD: StyleTheme = {
  id: "emerald",
  name: "Emerald",
  tagline: "Fresh, focused, growth-coded.",
  description:
    "Vivid emerald — energy without being loud, ambition without being corporate. Pairs well with the 'progress, streaks, levelling up' loop your dashboard runs on. Dark mode goes near-black with a faint green undertone and a mint-coloured accent.",
  accentNote: "Accent: emerald green.",
  vibes: ["Fresh", "Energetic", "Progress", "Focused"],
  light: {
    bg:               "248 250 249",
    surface:          "255 255 255",
    surface2:         "240 245 243",
    surface3:         "223 234 230",
    border:           "228 232 230",
    borderStrong:     "203 218 213",
    text:             "11 26 22",
    textMuted:        "68 88 80",
    textSubtle:       "143 162 155",
    brand:            "16 185 129",     // #10B981 emerald-500
    brandHover:       "5 150 105",
    brandSoft:        "209 250 229",
    brandSoftStrong:  "167 243 208",
    accent:           "217 119 6",
    accentSoft:       "254 243 199",
    success:          "16 185 129",
    successSoft:      "209 250 229",
    danger:           "220 38 38",
    dangerSoft:       "254 226 226",
    info:             "2 132 199",
    ring:             "110 231 183",
  },
  dark: {
    bg:               "8 18 14",        // #08120E near-black with green undertone
    surface:          "16 28 22",       // #101C16
    surface2:         "24 42 32",
    surface3:         "34 60 46",
    border:           "24 42 32",
    borderStrong:     "55 88 70",
    text:             "236 248 240",
    textMuted:        "163 192 175",
    textSubtle:       "105 128 116",
    brand:            "110 231 183",    // #6EE7B7 mint shift
    brandHover:       "153 246 209",
    brandSoft:        "12 40 26",
    brandSoftStrong:  "22 62 42",
    accent:           "251 191 36",
    accentSoft:       "60 42 12",
    success:          "76 200 142",
    successSoft:      "10 38 28",
    danger:           "240 122 122",
    dangerSoft:       "50 18 18",
    info:             "98 178 232",
    ring:             "52 211 153",
  },
};

// ---------------------------------------------------------------------------
// IRIS -- modern violet. More personality than indigo, less generic than
//   purple. Reads as smart and current. Dark mode is a warm-purple near-black
//   with a soft lilac accent that has gravity without glowing.
// ---------------------------------------------------------------------------
export const IRIS: StyleTheme = {
  id: "iris",
  name: "Iris",
  tagline: "Modern violet with personality.",
  description:
    "Vibrant but composed violet. The colour says 'thinks for itself' without being teen-coded the way pure purple can be. Dark mode is a deep warm-violet ground with a soft lilac accent — pulls the room slightly inward, late-evening study energy.",
  accentNote: "Accent: iris violet.",
  vibes: ["Modern", "Distinctive", "Smart", "Evening"],
  light: {
    bg:               "250 250 252",
    surface:          "255 255 255",
    surface2:         "245 243 252",
    surface3:         "234 230 248",
    border:           "230 228 240",
    borderStrong:     "208 204 226",
    text:             "20 14 35",
    textMuted:        "84 73 110",
    textSubtle:       "156 144 184",
    brand:            "124 58 237",     // #7C3AED violet-600
    brandHover:       "109 40 217",
    brandSoft:        "237 233 254",
    brandSoftStrong:  "221 214 254",
    accent:           "234 88 12",
    accentSoft:       "255 237 213",
    success:          "5 150 105",
    successSoft:      "209 250 229",
    danger:           "220 38 38",
    dangerSoft:       "254 226 226",
    info:             "2 132 199",
    ring:             "196 181 253",
  },
  dark: {
    bg:               "16 12 26",       // #100C1A warm purple near-black
    surface:          "26 22 40",       // #1A1628
    surface2:         "40 32 60",
    surface3:         "58 48 90",
    border:           "40 32 60",
    borderStrong:     "75 64 115",
    text:             "240 236 250",
    textMuted:        "175 165 198",
    textSubtle:       "118 110 145",
    brand:            "196 181 253",    // #C4B5FD soft lilac
    brandHover:       "221 214 254",
    brandSoft:        "35 26 70",
    brandSoftStrong:  "52 42 105",
    accent:           "251 191 36",
    accentSoft:       "60 42 12",
    success:          "76 200 142",
    successSoft:      "10 38 28",
    danger:           "240 122 122",
    dangerSoft:       "50 18 18",
    info:             "125 211 252",
    ring:             "167 139 250",
  },
};

// ---------------------------------------------------------------------------
// PULSE -- lifted directly from your existing staxed.in landing page. Pulse
//   orange (#F97316) is the brand colour you already own; using it in the
//   app gives you visual continuity between marketing and product. Status
//   colours shift to green/red/sky so the orange brand never collides with
//   "success" or "info" UI.
// ---------------------------------------------------------------------------
export const PULSE: StyleTheme = {
  id: "pulse",
  name: "Pulse",
  tagline: "Your existing staxed.in brand orange.",
  description:
    "The orange already on your landing page (#F97316 / pulse-500). Brand continuity from the moment a visitor lands at staxed.in to the moment they open the app. Light mode keeps the slate-neutral surrounds your landing already uses; dark mode is a warm near-black with the faintest orange undertone (#181210) and a lifted #FB923C accent so the brand stays unmistakable in either mode.",
  accentNote: "Accent: Pulse orange. Lifted from your landing page.",
  vibes: ["Brand-continuous", "Warm", "Energetic", "Confident"],
  light: {
    bg:               "255 255 255",     // white
    surface:          "255 255 255",
    surface2:         "255 247 237",     // #FFF7ED pulse-50 (very faint warm wash)
    surface3:         "255 237 213",     // #FFEDD5 pulse-100
    border:           "226 232 240",     // #E2E8F0 slate-200
    borderStrong:     "203 213 225",     // #CBD5E1 slate-300
    text:             "15 23 42",        // #0F172A slate-900
    textMuted:        "100 116 139",     // #64748B slate-500
    textSubtle:       "148 163 184",     // #94A3B8 slate-400
    brand:            "249 115 22",      // #F97316 pulse-500 -- the brand
    brandHover:       "234 88 12",       // #EA580C pulse-600
    brandSoft:        "255 237 213",     // #FFEDD5 pulse-100
    brandSoftStrong:  "254 215 170",     // #FED7AA pulse-200
    accent:           "249 115 22",
    accentSoft:       "255 247 237",
    success:          "22 163 74",       // #16A34A green-600 -- distinct from brand
    successSoft:      "220 252 231",
    danger:           "220 38 38",
    dangerSoft:       "254 226 226",
    info:             "14 165 233",      // #0EA5E9 sky-500
    ring:             "249 115 22",
  },
  dark: {
    bg:               "24 18 16",        // #181210 warm near-black, faint orange undertone
    surface:          "35 26 23",        // #231A17
    surface2:         "47 38 32",        // #2F2620
    surface3:         "62 50 43",        // #3E322B
    border:           "47 38 32",
    borderStrong:     "75 55 51",
    text:             "255 247 237",     // warm parchment, the pulse-50 lifted to fg
    textMuted:        "184 168 158",
    textSubtle:       "122 106 96",
    brand:            "251 146 60",      // #FB923C pulse-400 -- lifted for contrast on warm dark
    brandHover:       "253 186 116",     // #FDBA74 pulse-300
    brandSoft:        "63 30 10",        // deep amber surface
    brandSoftStrong:  "95 47 18",
    accent:           "251 146 60",
    accentSoft:       "63 30 10",
    success:          "74 222 128",      // #4ADE80 green-400
    successSoft:      "15 36 23",
    danger:           "248 113 113",
    dangerSoft:       "63 24 24",
    info:             "125 211 252",
    ring:             "251 146 60",
  },
};

// ---------------------------------------------------------------------------
// CORAL -- warm energetic. Friendly and inviting without being cute. Reads
//   as morning energy. Dark mode is a warm reddish near-black with a soft
//   salmon accent — cozy rather than alarming.
// ---------------------------------------------------------------------------
export const CORAL: StyleTheme = {
  id: "coral",
  name: "Coral",
  tagline: "Warm, friendly, morning-energy.",
  description:
    "Coral-pink with confidence. Adds warmth without sliding into 'cute' territory — friendly, approachable, the colour of a notebook tab you actually like opening. Dark mode is a warm reddish near-black with a salmon accent that feels cosy at midnight.",
  accentNote: "Accent: warm coral.",
  vibes: ["Warm", "Friendly", "Energetic", "Approachable"],
  light: {
    bg:               "252 250 250",
    surface:          "255 255 255",
    surface2:         "252 244 244",
    surface3:         "248 228 228",
    border:           "240 230 230",
    borderStrong:     "224 200 200",
    text:             "32 16 20",
    textMuted:        "98 76 80",
    textSubtle:       "168 145 150",
    brand:            "244 63 94",      // #F43F5E rose-500
    brandHover:       "225 29 72",
    brandSoft:        "255 228 230",
    brandSoftStrong:  "254 205 211",
    accent:           "194 65 12",
    accentSoft:       "255 237 213",
    success:          "5 150 105",
    successSoft:      "209 250 229",
    danger:           "153 27 27",
    dangerSoft:       "254 226 226",
    info:             "2 132 199",
    ring:             "253 164 175",
  },
  dark: {
    bg:               "24 15 17",       // #180F11 warm dark
    surface:          "36 24 27",       // #24181B
    surface2:         "52 36 40",
    surface3:         "75 50 58",
    border:           "52 36 40",
    borderStrong:     "92 65 73",
    text:             "248 240 240",
    textMuted:        "200 175 178",
    textSubtle:       "135 110 115",
    brand:            "253 164 175",    // #FDA4AF rose-300 lifted
    brandHover:       "254 205 211",
    brandSoft:        "70 28 36",
    brandSoftStrong:  "100 42 52",
    accent:           "251 191 36",
    accentSoft:       "60 42 12",
    success:          "76 200 142",
    successSoft:      "10 38 28",
    danger:           "240 122 122",
    dangerSoft:       "50 18 18",
    info:             "125 211 252",
    ring:             "252 165 175",
  },
};

// ---------------------------------------------------------------------------
// EDITORIAL DIRECTIONS (kept for comparison, not removed) -- these use a
// different layout language entirely (hairline borders, no card shadows,
// serif italic emphasis words). The styleguide page renders them with the
// EditorialPortal layout, while the SaaS themes above render with the
// IndigoPortal layout.
// ---------------------------------------------------------------------------

export const TERRACOTTA: StyleTheme = {
  id: "terracotta",
  name: "Terracotta",
  tagline: "Warm. Editorial. Lived-in.",
  description:
    "Off-white paper, near-black ink, a single warm clay accent. Feels like a thoughtfully-set textbook. The dark mode is a warm near-black — not slate, not cool — with parchment-cream text. Note: uses an editorial layout (hairline borders, no shadows), not the SaaS layout the rest use.",
  accentNote: "Accent: clay terracotta. Editorial layout.",
  vibes: ["Editorial", "Warm", "Approachable", "Library"],
  light: {
    bg:               "250 250 247",
    surface:          "255 255 255",
    surface2:         "243 241 236",
    surface3:         "232 229 222",
    border:           "228 227 220",
    borderStrong:     "200 197 188",
    text:             "14 14 12",
    textMuted:        "90 90 85",
    textSubtle:       "154 154 146",
    brand:            "164 81 28",
    brandHover:       "133 66 23",
    brandSoft:        "245 230 217",
    brandSoftStrong:  "232 209 187",
    accent:           "164 81 28",
    accentSoft:       "245 230 217",
    success:          "76 102 64",
    successSoft:      "226 234 219",
    danger:           "157 41 31",
    dangerSoft:       "245 220 215",
    info:             "70 90 110",
    ring:             "164 81 28",
  },
  dark: {
    bg:               "14 14 12",
    surface:          "24 24 20",
    surface2:         "35 35 31",
    surface3:         "45 44 39",
    border:           "35 35 31",
    borderStrong:     "60 58 51",
    text:             "245 243 237",
    textMuted:        "165 162 153",
    textSubtle:       "111 108 100",
    brand:            "212 138 75",
    brandHover:       "227 162 105",
    brandSoft:        "58 36 18",
    brandSoftStrong:  "82 50 24",
    accent:           "212 138 75",
    accentSoft:       "58 36 18",
    success:          "138 172 130",
    successSoft:      "28 42 26",
    danger:           "248 113 113",
    dangerSoft:       "59 18 18",
    info:             "143 165 191",
    ring:             "212 138 75",
  },
};

export const MOSS: StyleTheme = {
  id: "moss",
  name: "Moss",
  tagline: "Academic. Calm. Quietly serious.",
  description:
    "Same off-white paper, but the accent is a deep moss green — the feel of a serious reference book. Editorial layout.",
  accentNote: "Accent: deep moss green. Editorial layout.",
  vibes: ["Academic", "Calm", "Library", "Reference"],
  light: {
    bg:               "250 250 246",
    surface:          "255 255 255",
    surface2:         "241 241 236",
    surface3:         "228 229 220",
    border:           "228 227 220",
    borderStrong:     "200 200 190",
    text:             "14 18 12",
    textMuted:        "83 89 83",
    textSubtle:       "150 156 145",
    brand:            "61 90 58",
    brandHover:       "48 73 47",
    brandSoft:        "226 236 223",
    brandSoftStrong:  "200 222 197",
    accent:           "61 90 58",
    accentSoft:       "226 236 223",
    success:          "61 90 58",
    successSoft:      "226 236 223",
    danger:           "157 41 31",
    dangerSoft:       "245 220 215",
    info:             "70 90 110",
    ring:             "61 90 58",
  },
  dark: {
    bg:               "12 15 12",
    surface:          "22 25 20",
    surface2:         "31 36 25",
    surface3:         "41 48 33",
    border:           "31 36 25",
    borderStrong:     "55 62 45",
    text:             "243 245 236",
    textMuted:        "163 170 155",
    textSubtle:       "108 115 100",
    brand:            "138 172 130",
    brandHover:       "162 191 152",
    brandSoft:        "28 42 26",
    brandSoftStrong:  "44 60 40",
    accent:           "138 172 130",
    accentSoft:       "28 42 26",
    success:          "138 172 130",
    successSoft:      "28 42 26",
    danger:           "248 113 113",
    dangerSoft:       "59 18 18",
    info:             "143 165 191",
    ring:             "138 172 130",
  },
};

export const MIDNIGHT: StyleTheme = {
  id: "midnight",
  name: "Midnight",
  tagline: "Mature. Quiet. Confident.",
  description:
    "Same paper base, deep ink-navy accent — the colour of a Moleskine cover or a hardback dust jacket. Editorial layout.",
  accentNote: "Accent: deep ink navy. Editorial layout.",
  vibes: ["Mature", "Restrained", "Sophisticated", "Studious"],
  light: {
    bg:               "249 249 247",
    surface:          "255 255 255",
    surface2:         "239 240 243",
    surface3:         "226 228 233",
    border:           "223 224 228",
    borderStrong:     "195 198 207",
    text:             "12 14 21",
    textMuted:        "83 87 98",
    textSubtle:       "146 150 162",
    brand:            "42 52 112",
    brandHover:       "33 41 89",
    brandSoft:        "227 230 244",
    brandSoftStrong:  "203 209 232",
    accent:           "42 52 112",
    accentSoft:       "227 230 244",
    success:          "61 90 58",
    successSoft:      "226 236 223",
    danger:           "157 41 31",
    dangerSoft:       "245 220 215",
    info:             "42 52 112",
    ring:             "42 52 112",
  },
  dark: {
    bg:               "10 12 18",
    surface:          "20 23 31",
    surface2:         "28 32 48",
    surface3:         "40 46 67",
    border:           "31 35 48",
    borderStrong:     "60 68 92",
    text:             "240 241 245",
    textMuted:        "163 167 178",
    textSubtle:       "108 113 125",
    brand:            "155 166 224",
    brandHover:       "180 188 232",
    brandSoft:        "22 27 46",
    brandSoftStrong:  "32 40 70",
    accent:           "155 166 224",
    accentSoft:       "22 27 46",
    success:          "138 172 130",
    successSoft:      "28 42 26",
    danger:           "248 113 113",
    dangerSoft:       "59 18 18",
    info:             "155 166 224",
    ring:             "155 166 224",
  },
};

/** SaaS-style themes render with the rounded-card portal layout. */
export const SAAS_THEMES: StyleTheme[] = [INDIGO, COBALT, EMERALD, IRIS, CORAL, PULSE];

/** Editorial-style themes render with the hairline portal layout. */
export const EDITORIAL_THEMES: StyleTheme[] = [TERRACOTTA, MOSS, MIDNIGHT];

/** All themes the styleguide shows, in display order. */
export const ALL_THEMES: StyleTheme[] = [...SAAS_THEMES, ...EDITORIAL_THEMES];

/** Returns true when this theme should use the editorial (hairline) portal layout. */
export function isEditorialTheme(theme: StyleTheme): boolean {
  return EDITORIAL_THEMES.some((t) => t.id === theme.id);
}
